import React from 'react';
import { useTranslation } from 'utils/i18n';
import sharedTranslations from '../shared/translations';
import { spacing } from './legalStyles';
import { useDarkMode } from 'utils/darkMode';

/**
 * ContractParties
 * Displays the contracting parties section at the top of legal pages.
 * McKinsey/Palantir-inspired ultra-professional design for web view.
 * Shows Besper UG information and customer placeholder fields.
 * When user has agreed, this can be populated with actual user/tenant data.
 */
const ContractParties = () => {
  const { language } = useTranslation();
  const { isDarkMode } = useDarkMode();
  
  const t = sharedTranslations[language] || sharedTranslations.en;
  const contractParties = t?.legal?.contractParties || {};
  
  // Hyper-professional McKinsey/Palantir styling - ultra clean and minimal
  const containerStyle = {
    marginBottom: spacing.xl,
    padding: 0,
    backgroundColor: 'transparent'
  };
  
  const headingStyle = {
    fontSize: '20px',
    fontWeight: '600',
    margin: `0 0 ${spacing.lg} 0`,
    color: isDarkMode ? '#f9fafb' : '#111827',
    letterSpacing: '-0.02em',
    lineHeight: '1.25',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    padding: 0
  };
  
  const partiesContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: spacing.xl,
    marginBottom: spacing.lg,
    alignItems: 'flex-start'
  };
  
  const sectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md
  };
  
  const sectionLabelStyle = {
    fontSize: '10px',
    fontWeight: '700',
    color: isDarkMode ? '#6b7280' : '#6b7280',
    marginBottom: spacing.xs,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    lineHeight: '1.5'
  };
  
  const companyInfoStyle = {
    fontSize: '14px',
    lineHeight: '1.75',
    color: isDarkMode ? '#f9fafb' : '#111827',
    whiteSpace: 'pre-line',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontWeight: '400',
    letterSpacing: '0',
    margin: 0
  };
  
  const fieldContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md
  };
  
  const fieldGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  };
  
  const fieldLabelStyle = {
    fontSize: '11px',
    fontWeight: '600',
    color: isDarkMode ? '#6b7280' : '#6b7280',
    marginBottom: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    lineHeight: '1.4'
  };
  
  const fieldValueStyle = {
    fontSize: '14px',
    lineHeight: '1.6',
    color: isDarkMode ? '#4b5563' : '#9ca3af',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontWeight: '400',
    fontStyle: 'normal',
    letterSpacing: '0',
    margin: 0
  };
  
  const noticeStyle = {
    fontSize: '12px',
    color: isDarkMode ? '#6b7280' : '#6b7280',
    fontStyle: 'normal',
    marginTop: spacing.lg,
    paddingTop: spacing.md,
    borderTop: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'}`,
    lineHeight: '1.65',
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontWeight: '400',
    letterSpacing: '0',
    maxWidth: '700px'
  };
  
  return (
    <div style={containerStyle}>
      <h2 style={headingStyle}>
        {contractParties.heading || 'Contracting Parties'}
      </h2>
      
      <div style={partiesContainerStyle}>
        {/* Besper Section - Processor */}
        <div style={sectionStyle}>
          <div style={sectionLabelStyle}>
            {contractParties.processorLabel || 'Processor'}
          </div>
          <div style={companyInfoStyle}>
            {contractParties.besperCompany || 'Besper UG (haftungsbeschränkt)'}
            {'\n'}{contractParties.besperAddress || 'Allacher Str. 189'}
            {'\n'}{contractParties.besperCity || '80997 Munich, Germany'}
            {'\n'}{contractParties.besperManagingDirector || 'Managing Director: Wolfgang Sperger'}
            {'\n'}{contractParties.besperRegister || 'Commercial Register: HRB 295807 (Munich District Court)'}
          </div>
        </div>
        
        {/* Customer Section - Controller */}
        <div style={sectionStyle}>
          <div style={sectionLabelStyle}>
            {contractParties.controllerLabel || 'Controller (Customer)'}
          </div>
          <div style={fieldContainerStyle}>
            <div style={fieldGroupStyle}>
              <div style={fieldLabelStyle}>
                {contractParties.companyNameLabel || 'Company Name'}
              </div>
              <div style={fieldValueStyle}>
                {contractParties.companyNamePlaceholder || 'Company name'}
              </div>
            </div>
            <div style={fieldGroupStyle}>
              <div style={fieldLabelStyle}>
                {contractParties.addressLabel || 'Address'}
              </div>
              <div style={fieldValueStyle}>
                {contractParties.addressPlaceholder || 'Street address, City, Country'}
              </div>
            </div>
            <div style={fieldGroupStyle}>
              <div style={fieldLabelStyle}>
                {contractParties.tenantIdLabel || 'Tenant ID'}
              </div>
              <div style={fieldValueStyle}>
                {contractParties.tenantIdPlaceholder || 'Tenant identifier'}
              </div>
            </div>
            <div style={fieldGroupStyle}>
              <div style={fieldLabelStyle}>
                {contractParties.authorizedRepresentativeLabel || 'Authorized Representative'}
              </div>
              <div style={fieldValueStyle}>
                {contractParties.representativePlaceholder || 'Full name'}
              </div>
            </div>
            <div style={fieldGroupStyle}>
              <div style={fieldLabelStyle}>
                {contractParties.emailLabel || 'Email'}
              </div>
              <div style={fieldValueStyle}>
                {contractParties.emailPlaceholder || 'email@example.com'}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Authority Warranty Notice */}
      <div style={noticeStyle}>
        {contractParties.authorityWarranty || 'By accepting this agreement, you represent and warrant that you have the authority to bind your organization to these terms. If you do not have such authority, you must not accept this Agreement.'}
      </div>
    </div>
  );
};

export default ContractParties;

